import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CurrencyContext = createContext(null);

const SYMBOLS = { ZMW: "K", USD: "$", CNY: "¥" };
const DEFAULT_RATES = { USD: 0.038, CNY: 0.27 };

export function CurrencyProvider({ children }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [currency, setCurrencyState] = useState(() => localStorage.getItem("esm_currency") || "ZMW");

  const refreshSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (err) {
      console.debug("Settings fetch skipped:", err?.message || err);
    }
  }, []);

  useEffect(() => {
    if (user) refreshSettings();
    else setSettings(null);
  }, [user, refreshSettings]);

  const setCurrency = (c) => {
    localStorage.setItem("esm_currency", c);
    setCurrencyState(c);
  };

  const rates = useMemo(() => settings?.rates || DEFAULT_RATES, [settings]);
  const convert = useCallback(
    (zmw) => (currency === "ZMW" ? zmw : (zmw || 0) * (rates[currency] || 1)),
    [currency, rates]
  );
  const fmt = useCallback(
    (zmw) =>
      `${SYMBOLS[currency]} ${Number(convert(zmw) || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    [currency, convert]
  );

  const value = useMemo(
    () => ({ currency, setCurrency, settings, refreshSettings, convert, fmt, rates }),
    [currency, settings, refreshSettings, convert, fmt, rates]
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
